import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Form,
  FormLayout,
  TextField,
  Button,
  ButtonGroup,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  DataTable,
  Badge,
  Modal
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

const UserManagement = () => {
  const { changePassword, createAdmin, user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('change-password');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Change Password Form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Create Admin Form
  const [adminForm, setAdminForm] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await authService.getUsers();
      setUsers(response.users);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteModalActive(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setLoading(true);
    try {
      await authService.deleteUser(userToDelete.id);
      setMessage('User deleted successfully!');
      await loadUsers(); // Reload users list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
      setDeleteModalActive(false);
      setUserToDelete(null);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    setMessage('');
    setError('');
  };

  const handleAdminChange = (field, value) => {
    setAdminForm(prev => ({ ...prev, [field]: value }));
    setMessage('');
    setError('');
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      if (result.success) {
        setMessage('Password changed successfully!');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (adminForm.password !== adminForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (adminForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (adminForm.username.length < 3) {
      setError('Username must be at least 3 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await createAdmin(adminForm.username, adminForm.password);
      if (result.success) {
        setMessage('Admin user created successfully!');
        setAdminForm({
          username: '',
          password: '',
          confirmPassword: ''
        });
        await loadUsers(); // Reload users list
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to create admin user');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    {
      id: 'change-password',
      content: 'Change Password',
      accessibilityLabel: 'Change Password',
      panelID: 'change-password-panel',
    },
    {
      id: 'create-admin',
      content: 'Create Admin',
      accessibilityLabel: 'Create Admin',
      panelID: 'create-admin-panel',
    },
    {
      id: 'list-users',
      content: 'List Users',
      accessibilityLabel: 'List Users',
      panelID: 'list-users-panel',
    },
  ];

  return (
    <Page title="User Management">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="200" align="space-between">
                <Text variant="headingMd" as="h2">
                  User Management
                </Text>
                <ButtonGroup>
                  {tabs.map((tab) => (
                    <Button
                      key={tab.id}
                      pressed={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.content}
                    </Button>
                  ))}
                </ButtonGroup>
              </InlineStack>

              {message && (
                <Banner tone="success" onDismiss={() => setMessage('')}>
                  {message}
                </Banner>
              )}

              {error && (
                <Banner tone="critical" onDismiss={() => setError('')}>
                  {error}
                </Banner>
              )}

              <Divider />

              {activeTab === 'change-password' && (
                <Card sectioned>
                  <Form>
                    <FormLayout>
                      <Text variant="headingMd" as="h3">
                        Change Password
                      </Text>
                      
                      <TextField
                        label="Current Password"
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(value) => handlePasswordChange('currentPassword', value)}
                        autoComplete="current-password"
                      />

                      <TextField
                        label="New Password"
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(value) => handlePasswordChange('newPassword', value)}
                        autoComplete="new-password"
                        helpText="Password must be at least 6 characters long"
                      />

                      <TextField
                        label="Confirm New Password"
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(value) => handlePasswordChange('confirmPassword', value)}
                        autoComplete="new-password"
                      />

                      <Button
                        variant="primary"
                        onClick={handleChangePassword}
                        loading={loading}
                        disabled={!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                      >
                        Change Password
                      </Button>
                    </FormLayout>
                  </Form>
                </Card>
              )}

              {activeTab === 'create-admin' && (
                <Card sectioned>
                  <Form>
                    <FormLayout>
                      <Text variant="headingMd" as="h3">
                        Create New Admin User
                      </Text>
                      
                      <TextField
                        label="Username"
                        value={adminForm.username}
                        onChange={(value) => handleAdminChange('username', value)}
                        autoComplete="username"
                        helpText="Username must be at least 3 characters long"
                      />

                      <TextField
                        label="Password"
                        type="password"
                        value={adminForm.password}
                        onChange={(value) => handleAdminChange('password', value)}
                        autoComplete="new-password"
                        helpText="Password must be at least 6 characters long"
                      />

                      <TextField
                        label="Confirm Password"
                        type="password"
                        value={adminForm.confirmPassword}
                        onChange={(value) => handleAdminChange('confirmPassword', value)}
                        autoComplete="new-password"
                      />

                      <Button
                        variant="primary"
                        onClick={handleCreateAdmin}
                        loading={loading}
                        disabled={!adminForm.username || !adminForm.password || !adminForm.confirmPassword}
                      >
                        Create Admin User
                      </Button>
                    </FormLayout>
                  </Form>
                </Card>
              )}

              {activeTab === 'list-users' && (
                <Card sectioned>
                  <BlockStack gap="400">
                    <Text variant="headingMd" as="h3">
                      Admin Users List
                    </Text>
                    
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text']}
                      headings={['Username', 'Created At', 'Status', 'Actions']}
                      rows={users.map(user => [
                        user.username,
                        new Date(user.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }),
                        user.id === currentUser?.id ? (
                          <Badge tone="info">Current User</Badge>
                        ) : (
                          <Badge tone="success">Active</Badge>
                        ),
                        user.id === currentUser?.id ? (
                          <Text variant="bodySm" tone="subdued">Cannot delete yourself</Text>
                        ) : (
                          <Button
                            size="slim"
                            variant="tertiary"
                            tone="critical"
                            onClick={() => handleDeleteUser(user)}
                          >
                            Delete
                          </Button>
                        )
                      ])}
                    />
                  </BlockStack>
                </Card>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={deleteModalActive}
        onClose={() => setDeleteModalActive(false)}
        title="Delete User"
        primaryAction={{
          content: 'Delete',
          onAction: confirmDeleteUser,
          loading: loading,
          destructive: true
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteModalActive(false)
          }
        ]}
      >
        <Modal.Section>
          <Text variant="bodyMd">
            Are you sure you want to delete user "{userToDelete?.username}"? 
            This action cannot be undone and will delete all associated data.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default UserManagement;
